// Web Audio API sound manager — retro arcade tones generated on-the-fly.
// Single shared AudioContext. No external audio files.
//
// Volume routing:
//   - All SFX flow through `sfxMasterGain` so a single slider can attenuate
//     every blip without changing per-sound balance.
//   - `startMusic()` builds its own subgraph rooted at `musicMasterGain`
//     for the same reason — sliding music volume doesn't squash SFX.

import { createContext, useContext, useEffect, useMemo, useState } from "react";

let ctx = null;
let musicNodes = null;
let sfxMasterGain = null;   // SFX master volume node
let musicMasterGain = null; // Music master volume node (rebuilt with each track)

// Persisted volume preferences (0..1). Read once at module import time.
function readVol(key, fallback) {
  if (typeof localStorage === "undefined") return fallback;
  const v = parseFloat(localStorage.getItem(key) || "");
  return Number.isFinite(v) && v >= 0 && v <= 1 ? v : fallback;
}
let sfxVolume = readVol("cwb_sfx_vol", 0.8);
let musicVolume = readVol("cwb_music_vol", 0.55);

function getCtx() {
  if (ctx) return ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  // Build the persistent SFX master node so every SFX uses the same
  // attenuation handle. The gain is multiplied by sfxVolume.
  sfxMasterGain = ctx.createGain();
  sfxMasterGain.gain.value = sfxVolume;
  sfxMasterGain.connect(ctx.destination);
  return ctx;
}

// Resolve the SFX bus — every envelopeBeep/noiseBurst connects through
// this so the master slider attenuates the whole SFX layer.
function sfxBus() {
  getCtx();
  return sfxMasterGain || ctx.destination;
}

function envelopeBeep({ freq = 600, type = "square", duration = 0.08, volume = 0.15, slideTo = null }) {
  const c = getCtx();
  if (!c) return;
  if (c.state === "suspended") c.resume();
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, c.currentTime);
  if (slideTo != null) osc.frequency.exponentialRampToValueAtTime(Math.max(1, slideTo), c.currentTime + duration);
  gain.gain.setValueAtTime(0, c.currentTime);
  gain.gain.linearRampToValueAtTime(volume, c.currentTime + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + duration);
  osc.connect(gain).connect(sfxBus());
  osc.start();
  osc.stop(c.currentTime + duration + 0.02);
}

function noiseBurst({ duration = 0.08, volume = 0.12 }) {
  const c = getCtx();
  if (!c) return;
  if (c.state === "suspended") c.resume();
  const bufferSize = Math.floor(c.sampleRate * duration);
  const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  const src = c.createBufferSource();
  src.buffer = buffer;
  const gain = c.createGain();
  gain.gain.setValueAtTime(volume, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + duration);
  src.connect(gain).connect(sfxBus());
  src.start();
  src.stop(c.currentTime + duration + 0.02);
}

let mutedFlag = false;
// Halloween 2026 — when set true (via `setSpookyPack(true)`), the
// standard destroyBlock SFX is replaced with the spooky gourd-thunk
// variant. Toggled by GameCanvas.jsx based on skin + bundle ownership.
let spookyPackFlag = false;
export function setSpookyPack(on) { spookyPackFlag = !!on; }
export function isSpookyPack() { return spookyPackFlag; }
function guard(fn) {
  if (mutedFlag) return;
  try {
    fn();
  } catch (_) {
    /* ignore */
  }
}

export const Sounds = {
  setMuted(m) {
    mutedFlag = !!m;
    if (mutedFlag) stopMusic();
  },
  isMuted() {
    return mutedFlag;
  },
  bouncePaddle() {
    guard(() => envelopeBeep({ freq: 520, type: "square", duration: 0.06, volume: 0.12 }));
  },
  bounceWall() {
    guard(() => envelopeBeep({ freq: 340, type: "square", duration: 0.05, volume: 0.08 }));
  },
  hitBlock() {
    guard(() => envelopeBeep({ freq: 720, type: "triangle", duration: 0.07, volume: 0.1 }));
  },
  destroyBlock() {
    // Route through the spooky pack when the Halloween bundle is
    // active — same call site, seasonal payoff.
    if (spookyPackFlag) {
      guard(() => {
        envelopeBeep({ freq: 560, type: "triangle", duration: 0.09, volume: 0.11, slideTo: 180 });
        setTimeout(() => noiseBurst({ duration: 0.08, volume: 0.06 }), 30);
        setTimeout(() => envelopeBeep({ freq: 260, type: "sine", duration: 0.14, volume: 0.09, slideTo: 90 }), 55);
      });
      return;
    }
    guard(() => {
      envelopeBeep({ freq: 880, type: "sawtooth", duration: 0.12, volume: 0.12, slideTo: 220 });
      noiseBurst({ duration: 0.1, volume: 0.08 });
    });
  },
  paddleMove() {
    guard(() => envelopeBeep({ freq: 180, type: "sine", duration: 0.025, volume: 0.03 }));
  },
  powerUp() {
    guard(() => {
      [440, 660, 880, 1100].forEach((f, i) =>
        setTimeout(() => envelopeBeep({ freq: f, type: "square", duration: 0.08, volume: 0.14 }), i * 55),
      );
    });
  },
  extraLife() {
    guard(() => {
      [523, 659, 784, 1047].forEach((f, i) =>
        setTimeout(() => envelopeBeep({ freq: f, type: "triangle", duration: 0.1, volume: 0.14 }), i * 70),
      );
    });
  },
  loseLife() {
    guard(() => {
      envelopeBeep({ freq: 420, type: "sawtooth", duration: 0.35, volume: 0.16, slideTo: 80 });
    });
  },
  gameOver() {
    guard(() => {
      [440, 349, 262, 175].forEach((f, i) =>
        setTimeout(() => envelopeBeep({ freq: f, type: "sawtooth", duration: 0.22, volume: 0.18 }), i * 140),
      );
    });
  },
  win() {
    guard(() => {
      [523, 659, 784, 1047, 1319].forEach((f, i) =>
        setTimeout(() => envelopeBeep({ freq: f, type: "square", duration: 0.14, volume: 0.18 }), i * 110),
      );
    });
  },
  skeletonHit() {
    // Bone rattle — three quick high-frequency ticks with a filtered
    // noise burst. Evokes a skull/bone clatter without any real sample.
    guard(() => {
      [1400, 1800, 2200].forEach((f, i) =>
        setTimeout(() => envelopeBeep({ freq: f, type: "square", duration: 0.035, volume: 0.07 }), i * 22),
      );
      noiseBurst({ duration: 0.08, volume: 0.05 });
    });
  },
  skeletonKill() {
    // Cracked-skull thunk — sharp low-mid impact, then a short noise
    // crackle (the "crack"), then a descending bone-pitch slide. Reads
    // clearly as "this thing just shattered" without the fanfare of a
    // full boss kill.
    guard(() => {
      envelopeBeep({ freq: 220, type: "sawtooth", duration: 0.12, volume: 0.18, slideTo: 90 });
      setTimeout(() => noiseBurst({ duration: 0.14, volume: 0.12 }), 40);
      setTimeout(() => envelopeBeep({ freq: 620, type: "square", duration: 0.09, volume: 0.11, slideTo: 180 }), 110);
    });
  },
  // --- Halloween 2026 Spooky Sound Pack ---
  // Alt brick-break: a hollow gourd-thunk with a witchy descending
  // pitch and a puff of filtered noise. Played instead of destroyBlock
  // while the halloween-comet skin is equipped AND the pilot owns the
  // Halloween 2026 bundle.
  spookyBrick() {
    guard(() => {
      envelopeBeep({ freq: 560, type: "triangle", duration: 0.09, volume: 0.11, slideTo: 180 });
      setTimeout(() => noiseBurst({ duration: 0.08, volume: 0.06 }), 30);
      setTimeout(() => envelopeBeep({ freq: 260, type: "sine", duration: 0.14, volume: 0.09, slideTo: 90 }), 55);
    });
  },
  // Pumpkin bomb explosion — a low bomb "whumph" plus a sizzle tail.
  pumpkinBoom() {
    guard(() => {
      envelopeBeep({ freq: 160, type: "sawtooth", duration: 0.22, volume: 0.18, slideTo: 40 });
      noiseBurst({ duration: 0.22, volume: 0.14 });
      setTimeout(() => envelopeBeep({ freq: 90, type: "sine", duration: 0.14, volume: 0.12, slideTo: 30 }), 60);
    });
  },
  // Witch cackle — plays once when the bundle unlocks (purchase success).
  witchCackle() {
    guard(() => {
      const notes = [880, 780, 920, 720, 990, 620];
      notes.forEach((f, i) => setTimeout(() => envelopeBeep({ freq: f, type: "triangle", duration: 0.07, volume: 0.12 }), i * 65));
    });
  },
  bossRoar() {
    // Boss entrance roar — fires alongside the cinematic title card.
    // Layers a deep sub-bass rumble that crescendos over ~800ms with a
    // filtered noise growl + descending wail to evoke a leviathan
    // bellow. Total ~2.0s so it tucks inside the 2.4s cinematic and
    // fades into the bgm naturally. No higher-pitched chime — this is
    // a threat, not a fanfare.
    guard(() => {
      const c = getCtx();
      if (!c) return;
      if (c.state === "suspended") c.resume();
      const now = c.currentTime;
      // 1. Deep sub-bass rumble — slow build from 50→95Hz, then sag.
      const rumble = c.createOscillator();
      rumble.type = "sine";
      rumble.frequency.setValueAtTime(50, now);
      rumble.frequency.exponentialRampToValueAtTime(95, now + 0.85);
      rumble.frequency.exponentialRampToValueAtTime(45, now + 1.9);
      const rumbleGain = c.createGain();
      rumbleGain.gain.setValueAtTime(0, now);
      rumbleGain.gain.linearRampToValueAtTime(0.32, now + 0.8);
      rumbleGain.gain.linearRampToValueAtTime(0.22, now + 1.4);
      rumbleGain.gain.exponentialRampToValueAtTime(0.0001, now + 2.0);
      rumble.connect(rumbleGain).connect(sfxBus());
      rumble.start(now);
      rumble.stop(now + 2.05);
      // 2. Sub-bass detune partner — a 5th below for menace thickness.
      const sub = c.createOscillator();
      sub.type = "sine";
      sub.frequency.setValueAtTime(33, now);
      sub.frequency.exponentialRampToValueAtTime(63, now + 0.85);
      sub.frequency.exponentialRampToValueAtTime(30, now + 1.9);
      const subGain = c.createGain();
      subGain.gain.setValueAtTime(0, now);
      subGain.gain.linearRampToValueAtTime(0.18, now + 0.8);
      subGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.95);
      sub.connect(subGain).connect(sfxBus());
      sub.start(now);
      sub.stop(now + 2.0);
      // 3. Filtered noise growl — low-passed white noise, slow swell.
      const bufferSize = Math.floor(c.sampleRate * 1.6);
      const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
      const noise = c.createBufferSource();
      noise.buffer = buffer;
      const filter = c.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.setValueAtTime(180, now);
      filter.frequency.linearRampToValueAtTime(420, now + 0.85);
      filter.frequency.linearRampToValueAtTime(140, now + 1.6);
      filter.Q.value = 8;
      const noiseGain = c.createGain();
      noiseGain.gain.setValueAtTime(0, now);
      noiseGain.gain.linearRampToValueAtTime(0.18, now + 0.8);
      noiseGain.gain.linearRampToValueAtTime(0.10, now + 1.3);
      noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.7);
      noise.connect(filter).connect(noiseGain).connect(sfxBus());
      noise.start(now);
      noise.stop(now + 1.7);
      // 4. Mid-range wail — descending sawtooth, fades in late and falls.
      const wail = c.createOscillator();
      wail.type = "sawtooth";
      wail.frequency.setValueAtTime(220, now + 0.45);
      wail.frequency.exponentialRampToValueAtTime(80, now + 1.6);
      const wailGain = c.createGain();
      wailGain.gain.setValueAtTime(0, now + 0.45);
      wailGain.gain.linearRampToValueAtTime(0.10, now + 0.95);
      wailGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.7);
      wail.connect(wailGain).connect(sfxBus());
      wail.start(now + 0.45);
      wail.stop(now + 1.75);
    });
  },
  bossDefeat() {
    // Multi-layer kill sound: heavy sub-bass impact → noise shatter →
    // descending boss-cry → ascending victory fanfare. Roughly 1.6s total,
    // so it overlaps the standard `win()` chime and clearly outweighs it.
    guard(() => {
      const c = getCtx();
      if (!c) return;
      if (c.state === "suspended") c.resume();
      const now = c.currentTime;
      // 1. Deep sub-bass impact (the kill blow)
      const impact = c.createOscillator();
      impact.type = "sine";
      impact.frequency.setValueAtTime(180, now);
      impact.frequency.exponentialRampToValueAtTime(40, now + 0.45);
      const impactGain = c.createGain();
      impactGain.gain.setValueAtTime(0, now);
      impactGain.gain.linearRampToValueAtTime(0.32, now + 0.02);
      impactGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      impact.connect(impactGain).connect(sfxBus());
      impact.start(now);
      impact.stop(now + 0.55);
      // 2. Shatter noise burst — 280ms of attenuating white noise
      const bufferSize = Math.floor(c.sampleRate * 0.28);
      const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
      }
      const src = c.createBufferSource();
      src.buffer = buffer;
      const noiseGain = c.createGain();
      noiseGain.gain.setValueAtTime(0.18, now);
      noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
      src.connect(noiseGain).connect(sfxBus());
      src.start(now);
      src.stop(now + 0.32);
      // 3. Boss-cry — sawtooth descending wail (its dying breath)
      const wail = c.createOscillator();
      wail.type = "sawtooth";
      wail.frequency.setValueAtTime(440, now + 0.1);
      wail.frequency.exponentialRampToValueAtTime(110, now + 0.85);
      const wailGain = c.createGain();
      wailGain.gain.setValueAtTime(0, now + 0.1);
      wailGain.gain.linearRampToValueAtTime(0.18, now + 0.15);
      wailGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.95);
      wail.connect(wailGain).connect(sfxBus());
      wail.start(now + 0.1);
      wail.stop(now + 1.0);
      // 4. Victory fanfare — 5-note ascending major triad chord shimmer
      const fanfare = [523, 659, 784, 1047, 1568];
      fanfare.forEach((freq, i) => {
        setTimeout(() => envelopeBeep({
          freq, type: "square", duration: 0.18, volume: 0.18,
        }), 600 + i * 120);
      });
      // 5. Final sustained gold-hit shimmer at the end
      setTimeout(() => {
        envelopeBeep({ freq: 2093, type: "triangle", duration: 0.4, volume: 0.14 });
      }, 1200);
      // 6. CINEMATIC CHORD SWELL — C major 4-voice stack played as a
      // simultaneous chord (root C5 / third E5 / fifth G5 / octave C6)
      // built from rich sawtooth voices through a low-pass filter for
      // warmth. Long ADSR (150ms attack, 400ms sustain, 700ms release)
      // turns the existing fanfare from "Mario coin pickup" into a
      // genuine "you DID it" dopamine swell. Lands 700ms into the
      // sequence so it crests just as the high shimmer hits.
      setTimeout(() => {
        const c2 = getCtx();
        if (!c2) return;
        const now2 = c2.currentTime;
        // Shared lowpass + bus gain so the chord blooms together.
        const filt = c2.createBiquadFilter();
        filt.type = "lowpass";
        filt.frequency.setValueAtTime(900, now2);
        filt.frequency.exponentialRampToValueAtTime(3200, now2 + 0.15);
        filt.Q.value = 1.2;
        const bus = c2.createGain();
        bus.gain.setValueAtTime(0, now2);
        bus.gain.linearRampToValueAtTime(0.22, now2 + 0.15); // attack
        bus.gain.setValueAtTime(0.22, now2 + 0.55);           // sustain
        bus.gain.exponentialRampToValueAtTime(0.001, now2 + 1.25); // release
        filt.connect(bus).connect(sfxBus());
        [523.25, 659.25, 783.99, 1046.5].forEach((f) => {
          const o = c2.createOscillator();
          o.type = "sawtooth";
          o.frequency.value = f;
          o.detune.value = (Math.random() - 0.5) * 8;
          o.connect(filt);
          o.start(now2);
          o.stop(now2 + 1.3);
        });
      }, 700);
    });
  },
  warp() {
    // Ascending shimmer + descending pitch-bend whoosh — teleport feel.
    guard(() => {
      const c = getCtx();
      if (!c) return;
      if (c.state === "suspended") c.resume();
      const now = c.currentTime;
      // Layer 1: rising triangle sweep 400→2400Hz (outward portal)
      {
        const osc = c.createOscillator();
        const g = c.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(400, now);
        osc.frequency.exponentialRampToValueAtTime(2400, now + 0.18);
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(0.14, now + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
        osc.connect(g).connect(sfxBus());
        osc.start(now);
        osc.stop(now + 0.25);
      }
      // Layer 2: descending sine 1600→180Hz (inward portal — slight delay)
      {
        const osc = c.createOscillator();
        const g = c.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(1600, now + 0.1);
        osc.frequency.exponentialRampToValueAtTime(180, now + 0.35);
        g.gain.setValueAtTime(0, now + 0.1);
        g.gain.linearRampToValueAtTime(0.18, now + 0.12);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.38);
        osc.connect(g).connect(sfxBus());
        osc.start(now + 0.1);
        osc.stop(now + 0.4);
      }
      // Layer 3: brief noise shimmer
      noiseBurst({ duration: 0.12, volume: 0.06 });
    });
  },
  blackhole() {
    // Deep sub-bass rumble with rapid amplitude wobble — "suck" effect.
    guard(() => {
      const c = getCtx();
      if (!c) return;
      if (c.state === "suspended") c.resume();
      const now = c.currentTime;
      const duration = 0.9;
      // Main body: sawtooth dropping 120→30Hz
      const body = c.createOscillator();
      body.type = "sawtooth";
      body.frequency.setValueAtTime(120, now);
      body.frequency.exponentialRampToValueAtTime(30, now + duration);
      const bodyGain = c.createGain();
      bodyGain.gain.setValueAtTime(0, now);
      bodyGain.gain.linearRampToValueAtTime(0.22, now + 0.05);
      bodyGain.gain.exponentialRampToValueAtTime(0.001, now + duration);
      // LFO modulating amplitude for the "pulse" effect
      const lfo = c.createOscillator();
      lfo.type = "sine";
      lfo.frequency.setValueAtTime(8, now);
      lfo.frequency.exponentialRampToValueAtTime(22, now + duration);
      const lfoGain = c.createGain();
      lfoGain.gain.value = 0.12;
      lfo.connect(lfoGain).connect(bodyGain.gain);
      body.connect(bodyGain).connect(sfxBus());
      body.start(now);
      body.stop(now + duration + 0.05);
      lfo.start(now);
      lfo.stop(now + duration + 0.05);
      // Quick noise sweep at the start for the "suck in" attack
      noiseBurst({ duration: 0.22, volume: 0.11 });
    });
  },
};

// ---------------------------------------------------------------------
// Sector-themed background music
// ---------------------------------------------------------------------
//
// Each of the 6 sectors gets a distinct ambient music bed built purely
// from oscillators + filtered noise (Web Audio API only — no asset
// downloads required). Themes vary in:
//   - musical scale (mode + root) so each sector has a unique tonality
//   - bass tone (sine / sawtooth / square) so harmonic colour shifts
//   - lead waveform + filter cutoff so the texture matures with risk
//   - tempo (step interval) — beginner is slow + dreamy, legendary is fast
//   - arp shape (rising / descending / pendulum) so motion differs
//   - pad shimmer (added high-octave sustain at higher sectors)
//
// All themes are loopable. `themeId` is the SECTOR id ("beginner" etc).
// Calling startMusic(sectorId) while playing the same theme is a no-op;
// switching to a different sector cleanly tears down and rebuilds the
// graph so the cross-sector transition stays glitch-free.
const MUSIC_THEMES = {
  beginner: {
    rootHz: 110,                                  // A2 — calm, deep
    scale: [0, 3, 5, 7, 10, 12, 10, 7],           // minor pentatonic w/ b7 colour
    bassWave: "sine",
    bassDetune: 0,
    leadWave: "triangle",
    filterHz: 1800,
    stepMs: 460,
    masterGain: 0.045,
    bassGain: 0.45,
    leadGain: 0.22,
    pad: false,
  },
  easy: {
    rootHz: 130.81,                               // C3 — bright + open
    scale: [0, 4, 7, 9, 12, 9, 7, 4],             // major triad arpeggio
    bassWave: "sine",
    bassDetune: -4,
    leadWave: "triangle",
    filterHz: 2200,
    stepMs: 420,
    masterGain: 0.05,
    bassGain: 0.48,
    leadGain: 0.24,
    pad: false,
  },
  medium: {
    rootHz: 146.83,                               // D3 — driving, gold
    scale: [0, 3, 7, 10, 7, 12, 10, 7],           // dorian — uplifting tension
    bassWave: "sawtooth",
    bassDetune: 0,
    leadWave: "square",
    filterHz: 2400,
    stepMs: 380,
    masterGain: 0.055,
    bassGain: 0.40,
    leadGain: 0.22,
    pad: true,
    padScale: [12, 19],                           // octave + fifth above
    padGain: 0.05,
  },
  hard: {
    rootHz: 164.81,                               // E3 — burning ember
    scale: [0, 2, 5, 7, 10, 12, 10, 5],           // mixolydian — heroic
    bassWave: "sawtooth",
    bassDetune: 7,                                // slight detune = thicker pulse
    leadWave: "square",
    filterHz: 2800,
    stepMs: 340,
    masterGain: 0.06,
    bassGain: 0.45,
    leadGain: 0.22,
    pad: true,
    padScale: [12, 19, 24],
    padGain: 0.06,
  },
  expert: {
    rootHz: 174.61,                               // F3 — crimson urgency
    scale: [0, 1, 4, 6, 7, 10, 6, 4],             // phrygian dominant — sinister
    bassWave: "sawtooth",
    bassDetune: -10,
    leadWave: "sawtooth",
    filterHz: 3200,
    stepMs: 310,
    masterGain: 0.065,
    bassGain: 0.5,
    leadGain: 0.24,
    pad: true,
    padScale: [7, 12, 19],
    padGain: 0.07,
  },
  legendary: {
    rootHz: 196,                                  // G3 — void ascendant
    scale: [0, 3, 6, 7, 11, 12, 11, 6],           // locrian-ish — alien
    bassWave: "square",
    bassDetune: -12,
    leadWave: "sawtooth",
    filterHz: 4000,
    stepMs: 280,
    masterGain: 0.07,
    bassGain: 0.55,
    leadGain: 0.26,
    pad: true,
    padScale: [12, 16, 19, 24],
    padGain: 0.08,
  },
};

const SECTOR_RANGES = [
  ["beginner",  1,    500],
  ["easy",      501,  1000],
  ["medium",    1001, 1500],
  ["hard",      1501, 2000],
  ["expert",    2001, 2500],
  ["legendary", 2501, 3000],
];

// Resolve a music theme id from either a level number or a sector id.
function resolveThemeId(input) {
  if (typeof input === "string") {
    return MUSIC_THEMES[input] ? input : "beginner";
  }
  const level = Math.max(1, Math.min(3000, Math.floor(Number(input) || 1)));
  const row = SECTOR_RANGES.find(([, from, to]) => level >= from && level <= to)
    || SECTOR_RANGES[0];
  return row[0];
}

// Convert a scale degree (semitone offset) to a frequency relative to root.
function semitoneFreq(rootHz, semitones) {
  return rootHz * 2 ** (semitones / 12);
}

/**
 * Start the looped ambient music bed for a given level or sector.
 * If music is already playing the SAME theme, this is a no-op. If a
 * different theme is requested, the existing bed is torn down before
 * the new one boots so the cross-fade stays glitch-free.
 *
 * @param {number|string} [levelOrSector=1] — global level (1..3000) or
 *   one of the sector ids ("beginner"|"easy"|...|"legendary").
 */
export function startMusic(levelOrSector = 1) {
  const c = getCtx();
  if (!c || mutedFlag) return;
  const themeId = resolveThemeId(levelOrSector);
  if (musicNodes && musicNodes.themeId === themeId) return;
  if (musicNodes) stopMusic();
  if (c.state === "suspended") c.resume();

  const theme = MUSIC_THEMES[themeId];

  // Master gain — multiplied by user's musicVolume preference.
  const master = c.createGain();
  master.gain.value = theme.masterGain * musicVolume;
  master.connect(c.destination);
  musicMasterGain = master;

  // Bass oscillator — slow constant drone at root frequency.
  const bass = c.createOscillator();
  bass.type = theme.bassWave;
  bass.frequency.value = theme.rootHz / 2;       // octave below root for depth
  bass.detune.value = theme.bassDetune;
  const bassGain = c.createGain();
  bassGain.gain.value = theme.bassGain;
  bass.connect(bassGain).connect(master);
  bass.start();

  // Lead arpeggio with a gentle low-pass filter so the texture isn't shrill.
  const lead = c.createOscillator();
  lead.type = theme.leadWave;
  const filter = c.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = theme.filterHz;
  filter.Q.value = 0.7;
  const leadGain = c.createGain();
  leadGain.gain.value = theme.leadGain;
  lead.connect(filter).connect(leadGain).connect(master);
  lead.start();

  // Optional shimmer pad (high sectors only) — slow sine drones on
  // upper-octave intervals add density without competing with the lead.
  const padOscs = [];
  if (theme.pad && theme.padScale) {
    theme.padScale.forEach((deg) => {
      const o = c.createOscillator();
      o.type = "sine";
      o.frequency.value = semitoneFreq(theme.rootHz, deg);
      const g = c.createGain();
      g.gain.value = theme.padGain;
      o.connect(g).connect(master);
      o.start();
      padOscs.push(o);
    });
  }

  // Arp loop — steps through the theme's scale degrees, applying them
  // to the lead oscillator. A subtle interval drift adds humanisation.
  let step = 0;
  const interval = setInterval(() => {
    if (!musicNodes) return;
    const now = c.currentTime;
    const deg = theme.scale[step % theme.scale.length];
    lead.frequency.setValueAtTime(semitoneFreq(theme.rootHz, deg) * 2, now);
    step++;
  }, theme.stepMs);

  musicNodes = { themeId, master, bass, lead, filter, padOscs, interval };
}

export function stopMusic() {
  if (!musicNodes) return;
  try {
    clearInterval(musicNodes.interval);
    musicNodes.bass.stop();
    musicNodes.lead.stop();
    (musicNodes.padOscs || []).forEach((o) => { try { o.stop(); } catch (_) {} });
    musicNodes.master.disconnect();
  } catch (_) {
    /* ignore */
  }
  musicNodes = null;
  musicMasterGain = null;
  // Side-effect: if a boss tension layer was running, kill it too.
  // The boss layer is conceptually a "module" of the music bed, so it
  // shouldn't survive a music teardown.
  stopTensionLayer();
}

// ---------------------------------------------------------------------
// Boss tension layer — crossfades a heavy filtered-noise drone + a
// dissonant minor-second oscillator cluster on top of the running
// music. Used to make boss-Leviathan levels feel weighty without
// changing the underlying sector theme. Crossfade is ~1.5s in/out so
// it lands like a wave rather than a jump-cut. Safe to call repeatedly
// — `startTensionLayer` is idempotent and `stopTensionLayer` is a
// no-op when no layer is active.
let tensionNodes = null;

export function startTensionLayer() {
  const c = getCtx();
  if (!c || mutedFlag || tensionNodes) return;
  if (c.state === "suspended") c.resume();
  const now = c.currentTime;
  // Master gain — fades in over 1.5s. Scaled by musicVolume so the
  // tension layer respects the same slider as the rest of the music.
  const master = c.createGain();
  master.gain.setValueAtTime(0, now);
  master.gain.linearRampToValueAtTime(0.05 * musicVolume, now + 1.5);
  master.connect(c.destination);

  // Filtered rumble — 2s loopable noise buffer through a low-pass.
  const bufferSize = Math.floor(c.sampleRate * 2.0);
  const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * 0.6;
  const noise = c.createBufferSource();
  noise.buffer = buffer;
  noise.loop = true;
  const noiseFilter = c.createBiquadFilter();
  noiseFilter.type = "lowpass";
  noiseFilter.frequency.value = 220;
  noiseFilter.Q.value = 4;
  const noiseGain = c.createGain();
  noiseGain.gain.value = 0.6;
  noise.connect(noiseFilter).connect(noiseGain).connect(master);
  noise.start(now);

  // Dissonant minor-second oscillator cluster — two sines a semitone
  // apart create slow beating that screams "something is wrong here."
  // Detuned slightly to add motion without being grating.
  const cluster = [55, 58.27].map((freq, idx) => {
    const o = c.createOscillator();
    o.type = "sine";
    o.frequency.value = freq;
    o.detune.value = idx === 0 ? -6 : 6;
    const g = c.createGain();
    g.gain.value = 0.5;
    o.connect(g).connect(master);
    o.start(now);
    return o;
  });

  tensionNodes = { master, noise, cluster };
}

export function stopTensionLayer() {
  if (!tensionNodes) return;
  const c = ctx;
  if (!c) {
    tensionNodes = null;
    return;
  }
  const now = c.currentTime;
  const { master, noise, cluster } = tensionNodes;
  tensionNodes = null;
  try {
    // Fade out over 1.5s, then stop nodes.
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(master.gain.value, now);
    master.gain.linearRampToValueAtTime(0.0001, now + 1.5);
    setTimeout(() => {
      try { noise.stop(); } catch (_) {}
      cluster.forEach((o) => { try { o.stop(); } catch (_) {} });
      try { master.disconnect(); } catch (_) {}
    }, 1700);
  } catch (_) {
    /* ignore — best-effort */
  }
}

// ---------------------------------------------------------------------
// Volume control API — separate sliders for SFX and Music.
// ---------------------------------------------------------------------
function clamp01(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

export function setSfxVolume(v) {
  sfxVolume = clamp01(v);
  if (typeof localStorage !== "undefined") {
    localStorage.setItem("cwb_sfx_vol", String(sfxVolume));
  }
  if (sfxMasterGain) sfxMasterGain.gain.value = sfxVolume;
}
export function getSfxVolume() { return sfxVolume; }

export function setMusicVolume(v) {
  musicVolume = clamp01(v);
  if (typeof localStorage !== "undefined") {
    localStorage.setItem("cwb_music_vol", String(musicVolume));
  }
  // Live-update the running music bed if any. We scale by the theme's
  // designed master to preserve the sector's relative loudness curve.
  if (musicNodes && musicMasterGain) {
    const t = MUSIC_THEMES[musicNodes.themeId] || MUSIC_THEMES.beginner;
    musicMasterGain.gain.value = t.masterGain * musicVolume;
  }
  // Also rescale the boss tension layer if it's running so the music
  // slider stays the single source of truth for all music-bus signals.
  if (tensionNodes && tensionNodes.master) {
    tensionNodes.master.gain.value = 0.05 * musicVolume;
  }
}
export function getMusicVolume() { return musicVolume; }

// ---------------------------------------------------------------------
// React hook + context for global mute + volume control
// ---------------------------------------------------------------------
const SoundCtx = createContext(null);

export function SoundProvider({ children }) {
  const [muted, setMuted] = useState(() => {
    const saved = localStorage.getItem("cwb_muted");
    return saved === "1";
  });
  const [sfxVol, setSfxVolState] = useState(sfxVolume);
  const [musicVol, setMusicVolState] = useState(musicVolume);

  useEffect(() => {
    Sounds.setMuted(muted);
    localStorage.setItem("cwb_muted", muted ? "1" : "0");
    if (muted) stopMusic();
  }, [muted]);

  const value = useMemo(
    () => ({
      muted,
      toggleMuted: () => setMuted((m) => !m),
      setMuted,
      sfxVol,
      musicVol,
      setSfxVol: (v) => {
        setSfxVolume(v);
        setSfxVolState(clamp01(v));
      },
      setMusicVol: (v) => {
        setMusicVolume(v);
        setMusicVolState(clamp01(v));
      },
    }),
    [muted, sfxVol, musicVol],
  );

  return <SoundCtx.Provider value={value}>{children}</SoundCtx.Provider>;
}

export function useSound() {
  const ctx = useContext(SoundCtx);
  if (!ctx) throw new Error("useSound must be used within SoundProvider");
  return ctx;
}
